<template>
  <div class="max-w-7xl mx-auto p-4 sm:p-6 md:p-8">
    <header class="mb-8">
      <h1 class="text-3xl sm:text-4xl font-bold text-slate-800 dark:text-slate-100 text-center sm:text-left">
        Consulta de Notícias
        <span class="text-purple-600 dark:text-purple-400">Financeiras</span>
      </h1>
      <p class="text-sm text-slate-600 dark:text-slate-400 text-center sm:text-left mt-1">
        Encontre as últimas notícias sobre finanças, investimentos e economia.
        <br>
        <span class="text-purple-600 dark:text-purple-400 font-semibold">Atualizado diariamente!</span>
      </p>
    </header>

    <div v-if="isLoading" class="space-y-12">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        <div class="h-80 bg-slate-200 dark:bg-slate-700 rounded-2xl animate-pulse"></div>
        <div class="space-y-4">
          <div class="h-8 bg-slate-200 dark:bg-slate-700 rounded-lg w-3/4 animate-pulse"></div>
          <div class="h-5 bg-slate-200 dark:bg-slate-700 rounded-lg w-full animate-pulse"></div>
          <div class="h-5 bg-slate-200 dark:bg-slate-700 rounded-lg w-1/2 animate-pulse"></div>
          <div class="h-10 bg-slate-200 dark:bg-slate-700 rounded-lg w-1/4 mt-4 animate-pulse"></div>
        </div>
      </div>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <div v-for="i in 6" :key="i" class="h-64 bg-slate-200 dark:bg-slate-700 rounded-2xl animate-pulse"></div>
      </div>
    </div>

    <div v-else-if="error" class="text-center py-12 bg-red-50 dark:bg-red-900/20 rounded-2xl">
      <span class="material-symbols-outlined text-6xl text-red-500">error</span>
      <h3 class="mt-4 text-2xl font-semibold text-red-700 dark:text-red-300">Falha ao carregar notícias</h3>
      <p class="mt-2 text-slate-600 dark:text-slate-400">Não foi possível buscar as informações. <br>Por favor, tente novamente mais tarde.</p>
    </div>

    <div v-else>
      <div class="mb-10 p-4 bg-white/70 dark:bg-slate-800/50 backdrop-blur-sm rounded-2xl border dark:border-slate-700">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div class="md:col-span-2">
            <label for="search" class="sr-only">Buscar notícias</label>
            <div class="relative">
              <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">search</span>
              <input type="text" id="search" v-model="searchQuery" placeholder="Buscar por título..."
                class="w-full pl-10 pr-4 py-2.5 text-sm rounded-lg bg-slate-100 dark:bg-slate-700 border-transparent focus:ring-2 focus:ring-purple-500 focus:border-transparent">
            </div>
          </div>
          <div class="col-span-1">
            <label for="category-filter" class="sr-only">Filtrar por Categoria</label>
             <select v-model="selectedCategory" id="category-filter" class="w-full px-4 py-2.5 text-sm rounded-lg bg-slate-100 dark:bg-slate-700 border-transparent focus:ring-2 focus:ring-purple-500 focus:border-transparent">
               <option value="">Todas as fontes</option>
               <option v-for="category in uniqueCategories" :key="category" :value="category">{{ category }}</option>
             </select>
          </div>
        </div>
      </div>
      
      <div v-if="featuredPost" class="mb-12 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        <a :href="featuredPost.externalUrl" target="_blank" rel="noopener noreferrer" class="group block rounded-2xl overflow-hidden shadow-lg">
           <img :src="featuredPost.imageUrl" :alt="featuredPost.title" class="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-105">
        </a>
        <div>
          <span class="text-sm font-bold uppercase tracking-widest text-purple-600 dark:text-purple-400">{{ featuredPost.categories[0] }}</span>
          <h2 class="mt-2 text-3xl lg:text-4xl font-bold text-slate-800 dark:text-slate-100">
            <a :href="featuredPost.externalUrl" target="_blank" rel="noopener noreferrer" class="hover:text-purple-700 dark:hover:text-purple-300 transition-colors">{{ featuredPost.title }}</a>
          </h2>
          <p class="mt-4 text-slate-600 dark:text-slate-400 leading-relaxed">
            {{ featuredPost.excerpt }}
          </p>
          <a :href="featuredPost.externalUrl" target="_blank" rel="noopener noreferrer" class="inline-flex items-center mt-6 text-white bg-purple-600 hover:bg-purple-700 font-semibold py-3 px-6 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg">
              Continuar Lendo
              <span class="material-symbols-outlined text-lg ml-2">arrow_forward</span>
          </a>
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        <div v-for="post in topPosts" :key="post.id"
          class="flex flex-col bg-white dark:bg-slate-800 rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:-translate-y-1 hover:shadow-xl border border-slate-200 dark:border-slate-700">
          <a :href="post.externalUrl" target="_blank" rel="noopener noreferrer" class="block h-48 w-full overflow-hidden group">
            <img :src="post.imageUrl" :alt="post.title" class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105">
          </a>
          <div class="p-5 flex flex-col flex-grow">
            <span class="text-xs font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400 mb-2">{{ post.categories[0] }}</span>
            <h3 class="text-lg font-bold text-slate-800 dark:text-slate-100 flex-grow">
              <a :href="post.externalUrl" target="_blank" rel="noopener noreferrer" class="hover:text-purple-700 dark:hover:text-purple-300 transition-colors">{{ post.title }}</a>
            </h3>
            <a :href="post.externalUrl" target="_blank" rel="noopener noreferrer" class="inline-flex items-center mt-4 text-sm font-semibold text-purple-600 dark:text-purple-400 group">
              Leia Mais
              <span class="material-symbols-outlined text-base ml-1 group-hover:translate-x-1 transition-transform">arrow_forward</span>
            </a>
          </div>
        </div>
      </div>
      
      <div v-if="remainingPosts.length > 0" class="mt-12 pt-10 border-t border-slate-200 dark:border-slate-700">
          <h2 class="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-8 text-center sm:text-left">Mais Notícias</h2>
          <div class="space-y-6">
              <div v-for="post in remainingPosts" :key="post.id"
                  class="flex flex-col sm:flex-row items-center bg-white dark:bg-slate-800 rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:bg-slate-50 dark:hover:bg-slate-700/50 border border-slate-200 dark:border-slate-700">
                  <a :href="post.externalUrl" target="_blank" rel="noopener noreferrer" class="block w-full sm:w-48 h-40 sm:h-full flex-shrink-0 group">
                      <img :src="post.imageUrl" :alt="post.title" class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105">
                  </a>
                  <div class="p-5 flex flex-col flex-grow">
                      <span class="text-xs font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400 mb-2">{{ post.categories[0] }}</span>
                      <h3 class="text-lg font-bold text-slate-800 dark:text-slate-100 flex-grow">
                          <a :href="post.externalUrl" target="_blank" rel="noopener noreferrer" class="hover:text-purple-700 dark:hover:text-purple-300 transition-colors">{{ post.title }}</a>
                      </h3>
                       <a :href="post.externalUrl" target="_blank" rel="noopener noreferrer" class="inline-flex items-center mt-4 text-sm font-semibold text-purple-600 dark:text-purple-400 group w-fit">
                          Leia Mais
                          <span class="material-symbols-outlined text-base ml-1 group-hover:translate-x-1 transition-transform">arrow_forward</span>
                       </a>
                  </div>
              </div>
          </div>
      </div>

       <div v-if="filteredPosts.length === 0 && !isLoading" class="text-center py-12">
            <span class="material-symbols-outlined text-6xl text-slate-400">search_off</span>
            <h3 class="mt-4 text-2xl font-semibold text-slate-600 dark:text-slate-300">Nenhum resultado encontrado</h3>
            <p class="mt-2 text-slate-500 dark:text-slate-400">Tente ajustar seus filtros ou termos de busca.</p>
        </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';

const allPosts = ref([]);
const isLoading = ref(true);
const error = ref(null);
const searchQuery = ref('');
const selectedCategory = ref('');

const filteredPosts = computed(() => {
  let posts = allPosts.value;
  if (searchQuery.value) {
    posts = posts.filter(post =>
      post.title.toLowerCase().includes(searchQuery.value.toLowerCase())
    );
  }
  if (selectedCategory.value) {
    posts = posts.filter(post => post.categories.includes(selectedCategory.value));
  }
  return posts;
});

const featuredPost = computed(() => filteredPosts.value[0] || null);
const topPosts = computed(() => filteredPosts.value.slice(1, 7)); // ATUALIZADO: Aumentei a grade principal para 6 posts
const remainingPosts = computed(() => filteredPosts.value.slice(7)); // NOVO: Pega o restante dos posts para a nova seção

const uniqueCategories = computed(() => {
    const categories = new Set();
    allPosts.value.forEach(post => {
        post.categories.forEach(cat => categories.add(cat));
    });
    return Array.from(categories).sort();
});

const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};

const fetchNews = async () => {
  isLoading.value = true;
  error.value = null;
  
  const topics = "finance";
  const apiKey = 'e988408d8c903da528b359541b19d02c';
  const url = `https://gnews.io/api/v4/search?q=(${topics})&lang=pt&country=br&max=100&apikey=${apiKey}`; // ATUALIZADO: max=100

  try {
    const response = await axios.get(url);
    if (response.data && response.data.articles) {
      const mappedPosts = response.data.articles.map(article => ({
        id: article.title + article.publishedAt,
        title: article.title,
        excerpt: article.description,
        imageUrl: article.image,
        externalUrl: article.url,
        categories: [article.source.name]
      }));
      allPosts.value = mappedPosts.filter(post => post.imageUrl && isValidUrl(post.imageUrl));
    }
  } catch (err) {
    console.error('Error fetching news:', err);
    error.value = 'Não foi possível carregar as últimas notícias.';
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchNews);
</script>

<style scoped>
</style>